import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminEmailTemplates = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_active: '',
    ...initialFilters
  });

  const activeStatusOptions = [
    { value: '', label: 'All Templates' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: templatesResponse, isLoading, error } = useQuery({
    queryKey: ['adminEmailTemplates', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/email-templates${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/email-templates', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminEmailTemplates']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/email-templates/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminEmailTemplates']);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/email-templates/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminEmailTemplates']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/email-templates/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminEmailTemplates']);
    }
  });

  const templates = useMemo(() => {
    return templatesResponse?.data?.data || templatesResponse?.data || [];
  }, [templatesResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(template => 
      template.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.html_content?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const statistics = useMemo(() => {
    const totalTemplates = templates.length;
    const activeTemplates = templates.filter(t => t.is_active).length;
    const inactiveTemplates = templates.filter(t => !t.is_active).length;

    return {
      totalTemplates,
      activeTemplates,
      inactiveTemplates
    };
  }, [templates]);

  return {
    templates,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    createTemplate: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateTemplate: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    toggleStatus: toggleStatusMutation.mutate,
    isToggling: toggleStatusMutation.isPending,
    deleteTemplate: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending
  };
};

